/**
 * Product Cost/Recipe Handlers
 * Pure functions for cost form validation and state transformations
 */

/**
 * Validate and build a stock entry to add to recipe
 * @param {Object} formCost - {count, stock, make}
 * @returns {Object} {valid: boolean, error: string|null, stock: Object|null, newFormCost: Object|null}
 */
export const validateAndBuildStock = (formCost) => {
  const { count, stock, make } = formCost;

  if (!stock) {
    return { valid: false, error: "Chọn 1 nguyên liệu để tạo" };
  }
  if (!count) {
    return { valid: false, error: `Cần thêm số lượng (${stock.unit}) cần sử dụng` };
  }

  const existsId = make?.filter((s) => s.id === stock.id);
  if (existsId && existsId.length > 0) {
    return { valid: false, error: "Đã chọn nguyên liệu này" };
  }

  const newStock = {
    id: stock.id,
    name: stock.name,
    unit: stock.unit,
    count: count,
  };

  const updatedMake = [...(make || []), newStock];

  return {
    valid: true,
    error: null,
    stock: newStock,
    newFormCost: {
      make: updatedMake,
      count: 0,
      unit: "",
      stock: null,
    },
  };
};

/**
 * Validate and build a NEW stock entry (for creating stock from product)
 * @param {Object} formCost - {count, stock, make_new, unit}
 * @returns {Object} {valid: boolean, error: string|null, newFormCost: Object|null}
 */
export const validateAndBuildNewStock = (formCost) => {
  const { count, stock, make_new, unit } = formCost;

  if (!stock) {
    return { valid: false, error: "Chọn 1 nguyên liệu để tạo" };
  }
  if (!count) {
    return { valid: false, error: `Cần thêm số lượng (${stock.unit}) cần sử dụng` };
  }

  const newStock = {
    name: stock.value,
    unit,
    count,
  };

  const updatedMakeNew = [...(make_new || []), newStock];

  return {
    valid: true,
    error: null,
    newFormCost: {
      make_new: updatedMakeNew,
      count: 0,
      unit: "",
      stock: null,
    },
  };
};

/**
 * Delete stock from recipe by id or name
 * @param {Object} formCost - Current form cost state
 * @param {string} id - Stock id or name to remove
 * @param {string} type - "name" | "id"
 * @returns {Object} {field: string, data: Array} - which field to update and with what
 */
export const removeStockFromRecipe = (formCost, id, type) => {
  if (type === "name") {
    return {
      field: "make_new",
      data: (formCost.make_new || []).filter((s) => s.name !== id),
    };
  } else if (type === "id") {
    return {
      field: "make",
      data: (formCost.make || []).filter((s) => s.id !== id),
    };
  }
  return null;
};

/**
 * Build new cost data from response after creating stocks
 * @param {Object} responseData - API response data
 * @param {Object} newCost - Single new cost object
 * @param {Array} makeNew - Array of new make items
 * @returns {Array} Updated make array with IDs
 */
export const buildCostDataFromResponse = (responseData, newCost, makeNew) => {
  if (newCost && Object.keys(newCost).length > 0) {
    if (responseData?.data?.id) {
      newCost["id"] = responseData.data.id;
      return [newCost];
    }
    console.error("Không thể lấy ID từ 'data' để cập nhật new_cost.");
    return [];
  }

  if (makeNew && makeNew.length > 0) {
    const createdStocks = Array.isArray(responseData?.data) ? responseData.data : [];
    return makeNew
      .map((item) => {
        const stock = createdStocks.find((s) => s.name === item.name);
        return { ...item, id: stock ? stock.id : null };
      })
      .filter((item) => item.id !== null);
  }

  return [];
};

/**
 * Build stock entries for creating stocks from product form
 * @param {Object} form - Product form state
 * @param {Array} makeNew - make_new array from form_cost
 * @returns {Array} Stock objects ready for API
 */
export const buildStockEntriesFromProduct = (form, makeNew) => {
  const cleanPrice = (val) => {
    if (!val && val !== 0) return "0";
    const num = parseFloat(String(val).replace(/,/g, ""));
    return isNaN(num) ? "0" : String(Math.trunc(num));
  };
  const priceCost = cleanPrice(form.price_cost);
  const quantity = cleanPrice(form.quantity);

  return makeNew.map((item) => ({
    name: item.name,
    unit: item.unit,
    count: quantity || 0,
    unit_price: priceCost || 0,
  }));
};
